import axios from 'axios';
import BaseApi from '../BaseApi';
import {
	UploadDocsFormTaskType,
	XmlHttpFileResponseType,
	UploadDocsFormResultType,
	SignInfoType,
	InFinishType,
} from 'src/Types/Task/UploadDocsForm';
import { TransitionCompleteResponse } from 'src/Types/Task';
import APICertificate from '../Certificate';

export default class UploadDocsFormAPI extends BaseApi {
	/** Получение данных задачи */
	static getData = async (
		fileId: string,
		componentId: string,
	): Promise<UploadDocsFormTaskType> => {
		const url = `${window.host}/Nvx.ReDoc.Workflow.UploadDocsForm/Task?fileId=${fileId}&componentId=${componentId}`;
		try {
			const request = await axios.get(url, { withCredentials: true });
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при получении данных задачи');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при получении данных задачи');
		}
	};

	/** Переоткрытие задачи */
	static reopen = async (
		fileId: string,
		componentId: string,
	): Promise<TransitionCompleteResponse> => {
		const url =
			window.host + '/Nvx.ReDoc.Workflow.UploadDocsForm/ReopenTask';
		const frmData = new FormData();
		frmData.append('fileId', fileId);
		frmData.append('componentId', componentId);
		try {
			const request = await axios.post(url, frmData, {
				withCredentials: true,
			});
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при переоткрытии задачи');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при переоткрытии задачи');
		}
	};

	/** запрос необходимости подписывать прикладываемые вложения */
	static checkNeedSignAttachments = async (
		fileId: string,
		componentId: string,
	): Promise<XmlHttpFileResponseType> => {
		const url =
			window.host +
			'/Nvx.ReDoc.Workflow.UploadDocsForm/CheckNeedSignAttachments';
		const frmData = new FormData();
		frmData.append('fileId', fileId);
		frmData.append('componentId', componentId);
		try {
			const request = await axios.post(url, frmData, {
				withCredentials: true,
			});
			if (this.checkUnauthorized(request)) {
				throw new Error(
					'Ошибка при запросе необходимости подписывать прикладываемые вложения',
				);
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error(
				'Ошибка при запросе необходимости подписывать прикладываемые вложения',
			);
		}
	};

	/** функция сбора документов с формы */
	static saveUploadDocsForm = async (
		frmData: FormData,
	): Promise<UploadDocsFormResultType> => {
		const url =
			window.host +
			'/Nvx.ReDoc.Workflow.UploadDocsForm/SaveUploadDocsForm';
		const thumbprint = frmData.get('thumbprint');
		if (thumbprint) {
			const cert = await APICertificate.getCertInfoByThumbprint(
				thumbprint.toString(),
			);
			frmData.append('certificate', cert.base64);
		}
		try {
			const request = await axios.post(url, frmData, {
				withCredentials: true,
			});
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при сборе документов с формы');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при сборе документов с формы');
		}
	};

	/** Сохранение подписей вложений */
	static _requestSaveSignAttachmentList = async (
		fileId: string,
		componentId: string,
		info: Array<SignInfoType>,
	): Promise<boolean> => {
		const url =
			window.host +
			'/Nvx.ReDoc.Workflow.UploadDocsForm/SaveNewSignAttachmentList';
		const frmData = new FormData();
		frmData.append('fileId', fileId);
		frmData.append('componentId', componentId);
		frmData.append('info', JSON.stringify(info));
		try {
			const request = await axios.post(url, frmData, {
				withCredentials: true,
			});
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при сохранении подписи вложения');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при сохранении подписи вложения');
		}
	};

	/** Окончание загрузки документов */
	static _requestFinishSaveUploadDocs = async (
		data: InFinishType,
	): Promise<TransitionCompleteResponse> => {
		const url =
			window.host +
			'/Nvx.ReDoc.Workflow.UploadDocsForm/FinishSaveUploadDocsForm';
		const frmData = new FormData();
		frmData.append('fileId', data.fileId);
		frmData.append('componentId', data.componentId!);
		frmData.append('uploadKey', data.uploadKey);
		frmData.append('commentary', data.commentary);
		frmData.append('docInfo', data.docInfo);
		if (data.issueDate) {
			frmData.append('issueDate', data.issueDate);
		}
		try {
			const request = await axios.post(url, frmData, {
				withCredentials: true,
			});
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка финишного шага загрузки документов');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка финишного шага загрузки документов');
		}
	};

	/** Подписывание документов, сохранение подписей и выполнение финального шага */
	static finishSaveUploadDocs = async (
		thumbprint: string,
		data: InFinishType,
		result: UploadDocsFormResultType,
	): Promise<TransitionCompleteResponse> => {
		const info: Array<SignInfoType> = [];
		if (result.info.length) {
			for (let index = 0; index < result.info.length; index++) {
				const doc = result.info[index];
				const signResult = await APICertificate.signHash(
					doc.hash,
					result.hashAlgId,
					thumbprint,
				);
				if (!signResult.success) {
					throw new Error('Ошибка подписи: ' + signResult.message);
				}
				info.push({
					attachmentId: doc.attachmentId,
					hash: signResult.data,
				});
			}
		}
		data.docInfo = result.docInfo;
		const isSuccess = await this._requestSaveSignAttachmentList(
			data.fileId,
			data.componentId!,
			info,
		);
		if (isSuccess) {
			return this._requestFinishSaveUploadDocs(data);
		} else {
			return {
				hasError: true,
				errorMessage: '',
				recid: data.fileId,
			};
		}
	};

	/** функция отмены задачи */
	static cancelTask = async (
		fileId: string,
		componentId: string,
	): Promise<TransitionCompleteResponse> => {
		const url =
			window.host + '/Nvx.ReDoc.Workflow.UploadDocsForm/CancelTask';
		const requestData = { fileId, componentId };
		try {
			const request = await axios.post(url, requestData, {
				withCredentials: true,
			});
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при отмене задачи');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при отмене задачи');
		}
	};

	/** прикладываю расписку и перехожу на след шаг */
	static goToNextStep = async (
		frmData: FormData,
	): Promise<UploadDocsFormResultType> => {
		const url =
			window.host +
			'/Nvx.ReDoc.Workflow.UploadDocsForm/SaveUploadDocsNote';
		try {
			const request = await axios.post(url, frmData, {
				withCredentials: true,
			});
			if (this.checkUnauthorized(request)) {
				throw new Error('Ошибка при переходе на следующий шаг');
			} else {
				return request.data;
			}
		} catch (error: any) {
			throw new Error('Ошибка при переходе на следующий шаг');
		}
	};
}
